// Copyright (c) 2026 Solartek LLC. All rights reserved.

import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Eye, Unplug } from 'lucide-react';
import { Button, EmptyState } from '@/components/ui';
import { useCan, useSurveillanceLink } from '@/features/me/use-me';
import { PRODUCTIVITY_NS } from '../i18n';

/** The Productivity screens need a connected link: until then they say so (and where to connect, for those who may). */
export function RequireSurveillanceLink({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation(PRODUCTIVITY_NS);
  const link = useSurveillanceLink();
  const can = useCan();
  if (link.connected) return <>{children}</>;
  return (
    <div className="page-container">
      <EmptyState icon={Unplug} title={t('notConnected.title')} description={t('notConnected.hint')}
        action={can('integration.manage') ? <Button asChild variant="outline"><Link to="/settings/surveillance">{t('notConnected.action')}</Link></Button> : undefined} />
    </div>
  );
}

/** The employee's own summary: a connected link that the organisation lets employees see (Settings → Flowza Surveillance). */
export function RequireSelfView({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation(PRODUCTIVITY_NS);
  const link = useSurveillanceLink();
  if (link.connected && link.employeeSelfView) return <>{children}</>;
  return <div className="page-container"><EmptyState icon={Eye} title={link.connected ? t('my.notShared') : t('notConnected.title')} /></div>;
}
